import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema, buildHowToStepsSchema } from "@/lib/schema";

export const Route = createFileRoute("/work-permit/type/$type")({
  loader: async ({ params }) => {
    const { WP_CASE_TYPES, getWpCaseType } = await import("@/content/work-permit-corpus/wp-types");
    const t = getWpCaseType(params.type);
    if (!t) throw notFound();
    return { t, siblings: WP_CASE_TYPES.filter((x) => x.slug !== t.slug).map((x) => ({ slug: x.slug, label: x.titleTh })) };
  },
  component: WpTypePage,
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "ไม่พบหน้า" }, { name: "robots", content: "noindex" }] };
    const site = getActiveSiteUrl();
    const { t } = loaderData;
    const faqs = [
      { q: `${t.titleTh} — ใครยื่นได้?`, a: t.whoTh },
      { q: "ลำดับขั้นตอนเป็นอย่างไร?", a: t.routeTh },
      { q: "หน่วยงานผู้พิจารณาคือใคร?", a: t.authorityTh },
      ...t.pitfallsTh.slice(0, 3).map((p) => ({ q: "ข้อผิดพลาดที่ทำให้เรื่องถูกตีกลับ", a: p })),
    ];
    return {
      ...pageHeadForProvince({
        title: `${t.titleTh} — ขั้นตอน เอกสาร และหน่วยงาน`,
        description: t.whoTh.slice(0, 155),
        path: `/work-permit/type/${params.type}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/work-permit/type/${params.type}` }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "ใบอนุญาตทำงาน", url: `${site}/work-permit/type` },
              { name: t.titleTh, url: `${site}/work-permit/type/${t.slug}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildHowToStepsSchema({ name: `ขั้นตอน${t.titleTh}`, description: t.routeTh, path: `/work-permit/type/${params.type}`, steps: t.processTh, inLanguage: "th" })) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/work-permit/type/${params.type}`)) },
      ],
    };
  },
});

function List({ title, items }: { title: string; items: string[] }) {
  return (
    <section className="mt-8">
      <h2 className="mb-3 text-xl font-bold">{title}</h2>
      <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
        {items.map((x: string, i: number) => <li key={i}>{x}</li>)}
      </ul>
    </section>
  );
}

function WpTypePage() {
  const { t, siblings } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/work-permit/type" className="hover:underline">ใบอนุญาตทำงาน</a> <span>/</span> <span>{t.titleTh}</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{t.titleTh}</h1>
        <p className="mb-6 text-muted-foreground">{t.whoTh}</p>
        <div className="rounded-lg border bg-muted/30 p-5">
          <h2 className="mb-2 text-lg font-bold">ลำดับขั้นตอนโดยสรุป</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{t.routeTh}</p>
        </div>
        <List title="เงื่อนไขที่ต้องผ่าน" items={t.requirementsTh} />
        <List title="เอกสารที่ต้องเตรียม" items={t.documentsTh} />
        <section className="mt-8">
          <h2 className="mb-3 text-xl font-bold">ขั้นตอนดำเนินการ</h2>
          <ol className="list-decimal space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground">
            {t.processTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ol>
        </section>
        <List title="ข้อผิดพลาดที่พบบ่อย" items={t.pitfallsTh} />
        <section className="mt-8">
          <h2 className="mb-2 text-xl font-bold">หน่วยงานผู้พิจารณา</h2>
          <p className="text-sm text-muted-foreground">{t.authorityTh}</p>
        </section>
        <section className="mt-8 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">ปรึกษาทีมงาน</h2>
          <p className="text-sm text-muted-foreground">
            สอบถามขอบเขตงาน เอกสารที่ต้องใช้ และระยะเวลาได้ทางโทรศัพท์ LINE หรืออีเมล
            เราไม่รับประกันผลการพิจารณาของหน่วยงาน แต่ช่วยตรวจเอกสารก่อนยื่นเพื่อลดโอกาสถูกตีกลับ
          </p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">ติดต่อเจ้าหน้าที่</a>
        </section>
        <section className="mt-8">
          <h2 className="mb-2 text-base font-bold">แหล่งอ้างอิง</h2>
          <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
            {t.sources.map((s: { label: string; url: string }) => (
              <li key={s.url}><a href={s.url} rel="nofollow noopener" target="_blank" className="underline">{s.label}</a></li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-muted-foreground">
            ข้อมูล ณ สิงหาคม 2569 (2026) โปรดตรวจสอบเงื่อนไขล่าสุดกับหน่วยงานก่อนดำเนินการ
          </p>
        </section>
        <section className="mt-10">
          <h2 className="mb-3 text-xl font-bold">กรณีอื่น</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {siblings.map((o: { slug: string; label: string }) => (
              <a key={o.slug} href={`/work-permit/type/${o.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {o.label}
              </a>
            ))}
          </div>
          <p className="mt-4 text-sm">
            เพิ่มเติม: <a href="/work-permit/faq" className="underline hover:text-gold">FAQ ใบอนุญาตทำงาน</a> ·{" "}
            <a href="/visa/thai" className="underline hover:text-gold">วีซ่าไทยทุกประเภท</a>
          </p>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
